// === [ CORETEX FYI ] ===
// Keep track if script is loaded
// #CB-A
window.PRODUCT_SCRIPT_LOADED = true;

// ----- [ constants.js
const DEBOUNCE_TIMER_ON_CHANGE = 300;

const PUB_SUB_EVENTS = {
  cartUpdate: 'cart-update',
  quantityUpdate: 'quantity-update',
  optionValueSelectionChange: 'option-value-selection-change',
  variantChange: 'variant-change',
  cartError: 'cart-error',
};

// ----- [ pubsub.js
let subscribers = {};

function subscribe(eventName, callback) {
  if (subscribers[eventName] === undefined) {
    subscribers[eventName] = [];
  }

  subscribers[eventName] = [...subscribers[eventName], callback];

  return function unsubscribe() {
    subscribers[eventName] = subscribers[eventName].filter((cb) => {
      return cb !== callback;
    });
  };
}

function publish(eventName, data) {
  if (subscribers[eventName]) {
    const promises = subscribers[eventName]
      .map((callback) => callback(data))
    return Promise.all(promises);
  } else {
    return Promise.resolve()
  }
}

// ----- [ global.js
function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

// document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => { ... }); // #CTL-NN

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

    // (gh) fixing issue #111
    // Code #CB-E
    onButtonClick(event) {
        event.preventDefault();

        // Use .closest() to find the nearest parent button, accounting for clicks on SVG icons or other children.
        const button = event.target.closest('button');
        const action = button.name; // 'plus' or 'minus'
        const previousValue = parseInt(this.input.value);
        const minValue = parseInt(this.input.dataset.min);

        if (action === 'plus') {
            if (previousValue < minValue) {
                this.input.value = minValue;
            } else {
                this.input.stepUp();
            }
        } else if (action === 'minus') {
            this.input.stepDown();
            if (parseInt(this.input.value) < minValue) {
                this.input.value = minValue;
            }
        }

        if (previousValue !== parseInt(this.input.value)) {
            this.input.dispatchEvent(this.changeEvent);
        }
    }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}
customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

//class MenuDrawer extends HTMLElement {} customElements.define('menu-drawer', MenuDrawer); // #CTL-NN
// class HeaderDrawer extends MenuDrawer {} // #CTL-NN
// class ModalDialog extends HTMLElement {} customElements.define('modal-dialog', ModalDialog); // #CTL-NN
// class BulkModal extends HTMLElement {} customElements.define('bulk-modal', BulkModal); // #CTL-NN
// class ModalOpener extends HTMLElement {} customElements.define('modal-opener', ModalOpener); // #CTL-NN

// #CCB-E
class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    // Check if there's a <video-player> element
    // If there's a <video-player>, do not run the rest of the logic
    const videoPlayer = this.querySelector('video-player');
    window.pauseAllMedia();

    if (!videoPlayer) {
      const template = this.querySelector('template');
      if (!template) {
        console.error('Template element not found.');
        return;
      }

      let deferredElement;

      if (!this.getAttribute('loaded')) {
        const content = document.createElement('div');
        content.appendChild(template.content.firstElementChild.cloneNode(true));

        this.setAttribute('loaded', true);
        deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
        if (focus) deferredElement.focus();
        if (deferredElement.nodeName === 'VIDEO' && deferredElement.getAttribute('autoplay')) {
          // Force autoplay for Safari
          deferredElement.play();
        }
      } else {
        // If already loaded, query the existing element
        deferredElement = this.querySelector('video, model-viewer, iframe');
      }

      // Workaround for Safari iframe bug (only run if deferredElement exists)
      if (deferredElement) {
        const formerStyle = deferredElement.getAttribute('style');
        deferredElement.setAttribute('style', 'display: block;');
        window.setTimeout(() => {
          deferredElement.setAttribute('style', formerStyle);
        }, 0);
      }
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

// class SliderComponent extends HTMLElement {} customElements.define('slider-component', SliderComponent); // #CTL-NN
// class SlideshowComponent extends SliderComponent { } customElements.define('slideshow-component', SlideshowComponent); // #CTL-NN

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

// former `product-recommendations.js`, now in `global.js`
class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        // if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
        //   this.remove();
        // }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

// class AccountIcon extends HTMLElement { ... } // #CTL-NN
// class BulkAdd extends HTMLElement {} if (!customElements.get('bulk-add')) { customElements.define('bulk-add', BulkAdd); } // #CTL-NN

// ----- [ pickup-availability.js
if (!customElements.get('pickup-availability')) { 
    customElements.define('pickup-availability',
    class PickupAvailability extends HTMLElement {
      constructor() {
        super();

        this.errorHtml = this.querySelector('template')?.content.firstElementChild?.cloneNode(true);
        this.onClickRefreshList = this.onClickRefreshList.bind(this);
        if (!this.hasAttribute('available')) return;
        this.fetchAvailability(this.dataset.variantId);
      }

      disconnectedCallback() {
        this.availabilityController?.abort();
      }

      fetchAvailability(variantId) {
        if (!variantId) return;
        this.dataset.variantId = variantId;
        this.availabilityController?.abort();
        const controller = this.availabilityController = new AbortController();
        let rootUrl = this.dataset.rootUrl || '/';
        if (!rootUrl.endsWith('/')) {
          rootUrl = rootUrl + '/';
        }
        const variantSectionUrl = `${rootUrl}variants/${variantId}/?section_id=x-pdp-pickup`; // #CTL-E
        // console.log(variantSectionUrl);

        fetch(variantSectionUrl, { signal: controller.signal })
          .then((response) => {
            if (!response.ok) throw new Error('Pickup availability unavailable');
            return response.text();
          })
          .then((text) => {
            const sectionInnerHTML = new DOMParser()
              .parseFromString(text, 'text/html')
              .querySelector('.shopify-section');
            if (!sectionInnerHTML) throw new Error('Missing pickup section');
            if (!controller.signal.aborted) this.renderPreview(sectionInnerHTML);
          })
          .catch((e) => {
            if (controller.signal.aborted) return;
            const button = this.querySelector('button');
            if (button) button.removeEventListener('click', this.onClickRefreshList);
            this.renderError();
          });
      }

      onClickRefreshList() {
        this.fetchAvailability(this.dataset.variantId);
      }

      update(variant) {
        if (variant?.available) {
          this.fetchAvailability(variant.id);
        } else {
          this.availabilityController?.abort();
          this.removeAttribute('available');
          this.innerHTML = '';
        }
      }

      renderError() {
        this.innerHTML = '';
        if (!this.errorHtml) { this.removeAttribute('available'); return; }
        this.appendChild(this.errorHtml.cloneNode(true));
        this.setAttribute('available', '');
        this.querySelector('button')?.addEventListener('click', this.onClickRefreshList);
      }

      renderPreview(sectionInnerHTML) {
        const drawer = document.querySelector('pickup-availability-drawer');
        if (drawer) drawer.remove();
        if (!sectionInnerHTML.querySelector('pickup-availability-preview')) {
          this.innerHTML = '';
          this.removeAttribute('available');
          return;
        }

        this.innerHTML = sectionInnerHTML.querySelector('pickup-availability-preview').outerHTML;
        this.setAttribute('available', '');

        const nextDrawer = sectionInnerHTML.querySelector('pickup-availability-drawer');
        if (nextDrawer) document.body.appendChild(nextDrawer);
        /* #CB-NN-CE
        const colorClassesToApply = this.dataset.productPageColorScheme.split(' ');
        colorClassesToApply.forEach((colorClass) => {
          document.querySelector('pickup-availability-drawer').classList.add(colorClass);
        });
        */

        const button = this.querySelector('button');
        if (button)
          button.addEventListener('click', (evt) => {
            document.querySelector('pickup-availability-drawer')?.show(evt.target);
          });
      }
    }
  );
}

if (!customElements.get('pickup-availability-drawer')) {
  customElements.define('pickup-availability-drawer',
    class PickupAvailabilityDrawer extends HTMLElement {
      constructor() {
        super();

        this.onBodyClick = this.handleBodyClick.bind(this);

        this.querySelector('button').addEventListener('click', () => {
          this.hide();
        });

        this.addEventListener('keyup', (event) => {
          if (event.code.toUpperCase() === 'ESCAPE') this.hide();
        });
      }

      handleBodyClick(evt) {
        const target = evt.target;
        if (
          target != this &&
          !target.closest('pickup-availability-drawer') &&
          target.id != 'ShowPickupAvailabilityDrawer'
        ) {
          this.hide();
        }
      }

      hide() {
        this.removeAttribute('open');
        document.body.removeEventListener('click', this.onBodyClick);
        document.body.classList.remove('overflow-hidden');
        removeTrapFocus(this.focusElement);
      }

      show(focusElement) {
        this.focusElement = focusElement;
        this.setAttribute('open', '');
        document.body.addEventListener('click', this.onBodyClick);
        document.body.classList.add('overflow-hidden');
        trapFocus(this);
      }
    }
  );
}

// recipient-form.js
if (!customElements.get('recipient-form')) {
  customElements.define(
    'recipient-form',
    class RecipientForm extends HTMLElement {
      constructor() {
        super();
        this.recipientFieldsLiveRegion = this.querySelector(`#Recipient-fields-live-region-${this.dataset.sectionId}`);
        this.checkboxInput = this.querySelector(`#Recipient-checkbox-${this.dataset.sectionId}`);
        this.checkboxInput.disabled = false;
        this.hiddenControlField = this.querySelector(`#Recipient-control-${this.dataset.sectionId}`);
        this.hiddenControlField.disabled = true;
        this.emailInput = this.querySelector(`#Recipient-email-${this.dataset.sectionId}`);
        this.nameInput = this.querySelector(`#Recipient-name-${this.dataset.sectionId}`);
        this.messageInput = this.querySelector(`#Recipient-message-${this.dataset.sectionId}`);
        this.sendonInput = this.querySelector(`#Recipient-send-on-${this.dataset.sectionId}`);
        this.offsetProperty = this.querySelector(`#Recipient-timezone-offset-${this.dataset.sectionId}`);
        if (this.offsetProperty) this.offsetProperty.value = new Date().getTimezoneOffset().toString();

        this.errorMessageWrapper = this.querySelector('.product-form__recipient-error-message-wrapper');
        this.errorMessageList = this.errorMessageWrapper?.querySelector('ul');
        this.errorMessage = this.errorMessageWrapper?.querySelector('.error-message');
        this.defaultErrorHeader = this.errorMessage?.innerText;
        this.currentProductVariantId = this.dataset.productVariantId;
        this.addEventListener('change', this.onChange.bind(this));
        this.onChange();
      }

      cartUpdateUnsubscriber = undefined;
      variantChangeUnsubscriber = undefined;
      cartErrorUnsubscriber = undefined;

      connectedCallback() {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event.source === 'product-form' && event.productVariantId.toString() === this.currentProductVariantId) {
            this.resetRecipientForm();
          }
        });

        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event.data.sectionId === this.dataset.sectionId) {
            this.currentProductVariantId = event.data.variant.id.toString();
          }
        });

        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartError, (event) => {
          if (event.source === 'product-form' && event.productVariantId.toString() === this.currentProductVariantId) {
            this.displayErrorMessage(event.message, event.errors);
          }
        });
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }

        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
        }

        if (this.cartErrorUnsubscriber) {
          this.cartErrorUnsubscriber();
        }
      }

      onChange() {
        if (this.checkboxInput.checked) {
          this.enableInputFields();
          // this.recipientFieldsLiveRegion.innerText = window.accessibilityStrings.recipientFormExpanded; // #CTL-NN
        } else {
          this.clearInputFields();
          this.disableInputFields();
          this.clearErrorMessage();
          // this.recipientFieldsLiveRegion.innerText = window.accessibilityStrings.recipientFormCollapsed; // #CTL-NN
        }
      }

      inputFields() {
        return [this.emailInput, this.nameInput, this.messageInput, this.sendonInput];
      }

      disableableFields() {
        return [...this.inputFields(), this.offsetProperty];
      }

      clearInputFields() {
        this.inputFields().forEach((field) => (field.value = ''));
      }

      enableInputFields() {
        this.disableableFields().forEach((field) => (field.disabled = false));
      }

      disableInputFields() {
        this.disableableFields().forEach((field) => (field.disabled = true));
      }

      displayErrorMessage(title, body) {
        this.clearErrorMessage();
        this.errorMessageWrapper.hidden = false;
        if (typeof body === 'object') {
          this.errorMessage.innerText = this.defaultErrorHeader;
          return Object.entries(body).forEach(([key, value]) => {
            const errorMessageId = `RecipientForm-${key}-error-${this.dataset.sectionId}`;
            const fieldSelector = `#Recipient-${key}-${this.dataset.sectionId}`;
            const message = `${value.join(', ')}`;
            const errorMessageElement = this.querySelector(`#${errorMessageId}`);
            const errorTextElement = errorMessageElement?.querySelector('.error-message');
            if (!errorTextElement) return;

            if (this.errorMessageList) {
              this.errorMessageList.appendChild(this.createErrorListItem(fieldSelector, message));
            }

            errorTextElement.innerText = `${message}.`;
            errorMessageElement.classList.remove('hidden');

            const inputElement = this[`${key}Input`];
            if (!inputElement) return;

            inputElement.setAttribute('aria-invalid', true);
            inputElement.setAttribute('aria-describedby', errorMessageId);
          });
        }

        this.errorMessage.innerText = body;
      }

      createErrorListItem(target, message) {
        const li = document.createElement('li');
        const a = document.createElement('a');
        a.setAttribute('href', target);
        a.innerText = message;
        li.appendChild(a);
        li.className = 'error-message';
        return li;
      }

      clearErrorMessage() {
        this.errorMessageWrapper.hidden = true;

        if (this.errorMessageList) this.errorMessageList.innerHTML = '';

        this.querySelectorAll('.recipient-fields .form__message').forEach((field) => {
          field.classList.add('hidden');
          const textField = field.querySelector('.error-message');
          if (textField) textField.innerText = '';
        });

        [this.emailInput, this.messageInput, this.nameInput, this.sendonInput].forEach((inputElement) => {
          inputElement.setAttribute('aria-invalid', false);
          inputElement.removeAttribute('aria-describedby');
        });
      }

      resetRecipientForm() {
        if (this.checkboxInput.checked) {
          this.checkboxInput.checked = false;
          this.clearInputFields();
          this.clearErrorMessage();
        }
      }
    }
  );
}

// product-form.js
if (!customElements.get('product-form')) {
	customElements.define(
		'product-form',
		class ProductForm extends HTMLElement {
			constructor() {
				super()

				this.form = this.querySelector('form')
				this.variantIdInput.disabled = false
				this.submitButton = this.querySelector('[type="submit"]')
				this.submitButtonText = this.submitButton.querySelector('span')

                // === [ CORETEX FYI ] ===
			    // === [ Custom Option Validation ] ===
                // #CB-A
				// Only bind validation if custom option inputs exist for this form
				this._hasCustomOptions = this._checkForCustomOptions()
				if (this._hasCustomOptions) {
					// Use capturing phase to intercept submit before ajax-cart-product-form
					this._boundSubmitHandler = this._handleFormSubmit.bind(this)
					this.form.addEventListener('submit', this._boundSubmitHandler, true)
				}
			}

            // === [ CORETEX FYI ] ===
            // === [ Custom Option Validation ] ===
            // #CB-A
            disconnectedCallback() {
				if (this._hasCustomOptions && this._boundSubmitHandler) this.form.removeEventListener('submit', this._boundSubmitHandler, true);
			}

			// Validates required custom option inputs before allowing cart submission
			_checkForCustomOptions() {
				const formId = this.form?.getAttribute('id')
				if (!formId) return false
				return document.querySelectorAll(`.customOptionInput[form="${formId}"]`).length > 0
			}

			_handleFormSubmit(event) {
				const validation = this._validateCustomOptions()

				if (!validation.valid) {
					// Stop the event from reaching ajax-cart-product-form
					event.preventDefault()
					event.stopImmediatePropagation()

					// Show validation errors
					this._showValidationErrors(validation.invalidInputs)
				}
			}

			_validateCustomOptions() {
				const formId = this.form?.getAttribute('id')
				if (!formId) return { valid: true, invalidInputs: [] }

				// Find all custom option inputs that belong to this form
				const customInputs = document.querySelectorAll(`.customOptionInput[form="${formId}"][required]`)
				const invalidInputs = []

				for (const input of customInputs) {
					let isValid = true

					if (input.type === 'checkbox') {
						// Checkbox must be checked if required
						isValid = input.checked
					} else {
						// Text input must have a non-empty value
						isValid = input.value.trim() !== ''
					}

					if (!isValid) invalidInputs.push(input);
				}

				return {
					valid: invalidInputs.length === 0,
					invalidInputs
				}
			}

			_showValidationErrors(invalidInputs) {
				for (const input of invalidInputs) {
					// Add invalid state
					input.setAttribute('aria-invalid', 'true')

					// Focus the first invalid input
					if (input === invalidInputs[0]) {
						input.focus()
						input.scrollIntoView({ behavior: 'smooth', block: 'center' })
					}

					// Remove invalid state on input change
					const removeInvalidState = () => {
						input.removeAttribute('aria-invalid')
						input.removeEventListener('input', removeInvalidState)
						input.removeEventListener('change', removeInvalidState)
					}

					input.addEventListener('input', removeInvalidState)
					input.addEventListener('change', removeInvalidState)
				}
			}

            // === [ CORETEX FYI ] ===
            // #CB-E
            // Added support for preorder attribute
            toggleSubmitButton(disable = true, text) {
                if (disable) {
                    this.submitButton.setAttribute('disabled', 'disabled')
                    if (text) this.submitButtonText.textContent = text
                } else {
                    this.submitButton.removeAttribute('disabled');

                    // Check if the "add to cart" button has preorder="true" attribute
                    const isPreOrder = this.submitButton.getAttribute('preorder') === 'true';

                    if (!isPreOrder) {
                        this.submitButtonText.textContent = window.variantStrings.addToCart
                    } else {
                        this.submitButtonText.textContent = window.variantStrings.preOrder
                    }
                }
            }

			get variantIdInput() {
				return this.form.querySelector('[name=id]')
			}
		}
	)
}

// product-info.js
if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));

        // HODAKYEE: keep Shopify's first product.media item active on initial
        // page load. Later variant changes still select their featured media
        // through updateMedia(), without changing the underlying list order.
        
      }

      // #CB-A: Embla Slider Initialization
      // Ensures that the appropriate image corresponding to the selected variant is displayed
      // when the page is loaded, by scrolling to the relevant image in the slider.

      // Returns the featured_media.id of the currently selected variant,
      // read from the pre-rendered JSON which is always accurate on page load.
      getSelectedVariantId() {
          const json = this.querySelector('[data-selected-variant]');
          if (json) {
              try {
                  const variant = JSON.parse(json.textContent);
                  return variant?.featured_media?.id ?? null;
              } catch {}
          }
          return null;
      }

      // #CB-A: Embla Slider
      // [On page load when media type is "Slider"]
      // Automatically scrolls the Embla slider to display the image associated with the selected variant.
      handleSlideSelection() {
        const inputVal = this.getSelectedVariantId();
        const slide = this.querySelector(`[thumbs-slide][data-media="${inputVal}"]`);
        if (slide) slide.click();
      }

      // [On page load when media type is "Stacked"]
      // Locates the selected variant media without changing gallery order.
      handleStackSelection() {
        const inputVal = this.getSelectedVariantId();
        if (inputVal) {
            const mediaId = `${this.dataset.section}-${inputVal}`; // Constructs media ID using section ID and variant attribute `coretex-id`
            this.querySelector('media-gallery')?.setActiveMedia?.(mediaId, false);
        }
      }


      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        // #CB-NN
        // if (!this.dataset.originalSection) {
        //   //UpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        // }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        // UpdateUnsubscriber?.();  // #CTL-NN
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        //productForm?.handleErrorMessage(); // #CTL-NN
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
            //document.querySelector(`#${targetId}`)?.focus({ preventScroll: true });
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks, [
            // Rebind dialog triggers after variant selector is updated
            () => {
              document.querySelectorAll('coretex-dialog').forEach((dialog) => {
                if (typeof dialog.rebindTriggers === 'function') {
                  dialog.rebindTriggers();
                }
              });
            }
          ]);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        // this.querySelector('share-button')?.updateUrl(
        //   `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        // );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        const mediaGallery = this.querySelector('media-gallery');
        const mediaGalleryDestinationRoot = html.querySelector('media-gallery');
        if (!mediaGallery || !mediaGalleryDestinationRoot) return;

        const mediaGallerySource = mediaGallery.querySelector('ul[slider-container]');
        const mediaGalleryDestination = mediaGalleryDestinationRoot.querySelector('ul[slider-container]');

        const galleryStateAttributes = ['mq', 'mq-s', 'loop', 'medialess'];
        for (const attribute of galleryStateAttributes) {
          const value = mediaGalleryDestinationRoot.getAttribute(attribute);
          if (value === null) mediaGallery.removeAttribute(attribute);
          else mediaGallery.setAttribute(attribute, value);
        }

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        const thumbnailSource = mediaGallery.querySelector('ul[control-thumbs]');
        const thumbnailDestination = mediaGalleryDestinationRoot.querySelector('ul[control-thumbs]');
        if (thumbnailSource && thumbnailDestination) {
          thumbnailSource.replaceChildren(...Array.from(thumbnailDestination.children));
        }

        const zoomDialog = this.querySelector('coretex-zoom-dialog[id^="imagezoom-"]');
        const zoomDialogDestination = html.querySelector('coretex-zoom-dialog[id^="imagezoom-"]');
        if (zoomDialog && zoomDialogDestination) {
          const zoomLists = ['mini-map ul', 'dialog main ul'];
          for (const selector of zoomLists) {
            const source = zoomDialog.querySelector(selector);
            const destination = zoomDialogDestination.querySelector(selector);
            if (source && destination) source.replaceChildren(...Array.from(destination.children));
          }
          zoomDialog.refresh?.();
        }

        mediaGallery.refresh?.();

        const fallbackMediaId = mediaGallery.querySelector('[data-media-id]')?.dataset.mediaId;
        const mediaId = variantFeaturedMediaId
          ? `${this.dataset.section}-${variantFeaturedMediaId}`
          : fallbackMediaId;
        mediaGallery.selectMedia?.(mediaId);
        zoomDialog?.rebindTriggers?.();
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}

// Custom sticky "quick add to cart" bar (PSYLOS1-style). Shows the bar once
// the real Add to cart button scrolls off-screen, hides it again near the
// footer, and mirrors the current price/variant selection read-only — the
// bar's own Add to cart button just triggers the real form.
class StickyBuyBar {
  constructor() {
    this.bar = document.getElementById('stickyBuyBar');
    if (!this.bar) return;

    this.formId = this.bar.getAttribute('data-product-form-id');
    this.form = this.formId ? document.getElementById(this.formId) : null;
    this.sectionId = this.bar.getAttribute('data-section-id');
    this.productInfo = this.form ? this.form.closest('product-info') : document.querySelector('product-info');
    // Scoped to this product's own form — never the first `.add-2-cart` in
    // the document, which could belong to a recommendation/quick-add card.
    this.triggerButton = this.form ? this.form.querySelector('.add-2-cart') : null;
    this.realSubmitButton = this.triggerButton;
    // Liquid Ajax Cart's `info.initiator` is the outer <ajax-cart-product-form>
    // element, not the inner native <form> — this is what request-start/end
    // events must be matched against, not this.form.
    this.realAjaxCartForm = this.realSubmitButton ? this.realSubmitButton.closest('ajax-cart-product-form') : null;

    this.hasPassedTrigger = false;
    this.atBottom = false;
    this.dismissed = false;

    this._observers = [];
    this._cleanupFns = [];
    this._variantChangeUnsub = null;
    this.realQuantityInput = null;
    this._onRealQuantityChange = () => this._syncQuantityFromReal();

    this._setupEventListeners();
    this._setupOptionSync();
    this._setupQuantitySync();
    this._setupVariantChangeSync();
    this._setupVisibilityObservers();
    this._setupHeightTracking();
    this._setupCartStateSync();
    this._syncAvailability();
  }

  // Mirrors the real form's in-flight add-to-cart state onto the sticky
  // bar's own visible button(s) — desktop and mobile share the same
  // [data-sticky-add-to-cart] selector, so both get the same treatment.
  // Scoped to this.realAjaxCartForm so an unrelated add-to-cart elsewhere
  // on the page (a recommendation card, Quick View) never touches this bar.
  _setupCartStateSync() {
    if (!this.realAjaxCartForm) return;

    const buttons = () => this.bar.querySelectorAll('[data-sticky-add-to-cart]');

    this._onCartRequestStart = (event) => {
      const { requestState } = event.detail;
      if (requestState.requestType !== 'add') return;
      if (requestState.info?.initiator !== this.realAjaxCartForm) return;

      buttons().forEach((btn) => {
        btn.disabled = true;
        const cta = btn.querySelector('.cta');
        const spinner = btn.querySelector('.loading__spinner');
        if (cta) {
          cta.dataset.idleText = cta.textContent;
          cta.textContent = window.variantStrings?.addingToCart || 'ADDING';
        }
        spinner?.classList.remove('hidden');
      });
    };

    this._onCartRequestEnd = (event) => {
      const { requestState } = event.detail;
      if (requestState.requestType !== 'add') return;
      if (requestState.info?.initiator !== this.realAjaxCartForm) return;

      buttons().forEach((btn) => {
        const cta = btn.querySelector('.cta');
        const spinner = btn.querySelector('.loading__spinner');
        if (cta && cta.dataset.idleText) {
          cta.textContent = cta.dataset.idleText;
          delete cta.dataset.idleText;
        }
        spinner?.classList.add('hidden');
      });
      // Real disabled/sold-out state (not just the loading flag) is
      // resynced right after via the form's own change/render pipeline —
      // _syncAvailability already runs on those triggers separately.
      this._syncAvailability();
    };

    document.addEventListener('liquid-ajax-cart:request-start', this._onCartRequestStart);
    document.addEventListener('liquid-ajax-cart:request-end', this._onCartRequestEnd);
    this._cleanupFns.push(() => {
      document.removeEventListener('liquid-ajax-cart:request-start', this._onCartRequestStart);
      document.removeEventListener('liquid-ajax-cart:request-end', this._onCartRequestEnd);
    });
  }

  // Keeps --stickyBarHeight live so the product-page back-to-top override
  // (t-product.css) can clear the bar's *real* rendered height instead of a
  // guessed constant — reacts to font/content/viewport changes for free.
  _setupHeightTracking() {
    const applyHeight = () => {
      document.documentElement.style.setProperty('--stickyBarHeight', `${this.bar.offsetHeight}px`);
    };
    applyHeight();

    if ('ResizeObserver' in window) {
      const resizeObserver = new ResizeObserver(applyHeight);
      resizeObserver.observe(this.bar);
      this._observers.push(resizeObserver);
    } else {
      window.addEventListener('resize', applyHeight);
      this._cleanupFns.push(() => window.removeEventListener('resize', applyHeight));
    }
  }

  // Shows the bar once the real Add to Cart button has scrolled fully past
  // the top of the viewport; hides it once a sentinel at the true end of
  // the footer (not the footer element itself — a tall footer would hide
  // the bar far too early) becomes visible; re-shows immediately on
  // scrolling back off that sentinel. IntersectionObserver-driven, so it
  // re-evaluates itself on resize/orientation change/layout shifts
  // (image loads, accordion expand) without any manual scroll math.
  _setupVisibilityObservers() {
    if (!this.triggerButton) return;

    if (!('IntersectionObserver' in window)) {
      this._setupVisibilityFallback();
      return;
    }

    const triggerObserver = new IntersectionObserver((entries) => {
      const entry = entries[entries.length - 1];
      const wasPassed = this.hasPassedTrigger;
      // Only counts as "passed" when it left through the TOP of the
      // viewport — scrolled past below the fold before the user has
      // reached it yet must not show the bar early.
      this.hasPassedTrigger = !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;

      // Scrolling back up to the original buy button clears a manual
      // dismiss, so closing the bar is a "not now", not a permanent opt-out.
      if (wasPassed && !this.hasPassedTrigger) this.dismissed = false;

      this._syncVisibility();
    }, { threshold: 0 });
    triggerObserver.observe(this.triggerButton);
    this._observers.push(triggerObserver);

    const sentinel = document.getElementById('stickyBarBottomSentinel');
    if (sentinel) {
      const bottomObserver = new IntersectionObserver((entries) => {
        this.atBottom = entries[entries.length - 1].isIntersecting;
        this._syncVisibility();
      }, { threshold: 0 });
      bottomObserver.observe(sentinel);
      this._observers.push(bottomObserver);
    }
  }

  // Only reached in browsers without IntersectionObserver support.
  _setupVisibilityFallback() {
    const update = () => {
      const wasPassed = this.hasPassedTrigger;
      this.hasPassedTrigger = this.triggerButton.getBoundingClientRect().bottom <= 0;
      if (wasPassed && !this.hasPassedTrigger) this.dismissed = false;

      const sentinel = document.getElementById('stickyBarBottomSentinel');
      this.atBottom = sentinel ? sentinel.getBoundingClientRect().top <= window.innerHeight : false;

      this._syncVisibility();
    };

    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    this._cleanupFns.push(() => window.removeEventListener('scroll', update));
    this._cleanupFns.push(() => window.removeEventListener('resize', update));
    update();
  }

  _syncVisibility() {
    const shouldShow = this.hasPassedTrigger && !this.atBottom && !this.dismissed;
    this.bar.classList.toggle('show', shouldShow);
  }

  _setupEventListeners() {
    this.bar.querySelectorAll('[data-sticky-add-to-cart]').forEach((button) => {
      const onClick = () => {
        const scope = button.closest('.stickyBuyBar__inner, .stickyBuyBar__mobile');
        const stickyInput = scope?.querySelector('[data-sticky-quantity-input]');
        if (stickyInput) this._setRealQuantity(stickyInput.value);
        if (this.realSubmitButton) this.realSubmitButton.click();
      };
      button.addEventListener('click', onClick);
      this._cleanupFns.push(() => button.removeEventListener('click', onClick));
    });

    const closeButton = this.bar.querySelector('[data-sticky-close]');
    if (closeButton) {
      const onClose = () => { this.dismissed = true; this._syncVisibility(); };
      closeButton.addEventListener('click', onClose);
      this._cleanupFns.push(() => closeButton.removeEventListener('click', onClose));
    }
  }

  // Mirrors the sticky quantity controls to the real product form so the
  // theme's existing validation and Ajax Cart submission remain authoritative.
  _setupQuantitySync() {
    this._bindRealQuantityInput();

    this.bar.querySelectorAll('[data-sticky-quantity-adjust]').forEach((button) => {
      const onAdjust = () => {
        const currentInput = button.closest('[data-sticky-quantity]')?.querySelector('[data-sticky-quantity-input]');
        if (!currentInput) return;
        const direction = Number(button.dataset.stickyQuantityAdjust) || 0;
        const step = Number(currentInput.step) || 1;
        this._setRealQuantity(Number(currentInput.value) + (direction * step));
      };
      button.addEventListener('click', onAdjust);
      this._cleanupFns.push(() => button.removeEventListener('click', onAdjust));
    });

    this.bar.querySelectorAll('[data-sticky-quantity-input]').forEach((input) => {
      const onChange = () => this._setRealQuantity(input.value);
      input.addEventListener('change', onChange);
      this._cleanupFns.push(() => input.removeEventListener('change', onChange));
    });
  }

  _getRealQuantityInput() {
    const quantityInput = Array.from(document.querySelectorAll('input.quantity__input[name="quantity"]'))
      .find((input) => input.form?.id === this.formId);
    if (quantityInput) return quantityInput;

    if (!this.form) return null;
    let proxyInput = this.form.querySelector('input[data-sticky-quantity-proxy]');
    if (!proxyInput) {
      proxyInput = document.createElement('input');
      proxyInput.type = 'hidden';
      proxyInput.name = 'quantity';
      proxyInput.value = '1';
      proxyInput.dataset.stickyQuantityProxy = '';
      this.form.appendChild(proxyInput);
    }
    return proxyInput;
  }

  _bindRealQuantityInput() {
    const nextInput = this._getRealQuantityInput();
    if (nextInput !== this.realQuantityInput) {
      this.realQuantityInput?.removeEventListener('input', this._onRealQuantityChange);
      this.realQuantityInput?.removeEventListener('change', this._onRealQuantityChange);
      this.realQuantityInput = nextInput;
      this.realQuantityInput?.addEventListener('input', this._onRealQuantityChange);
      this.realQuantityInput?.addEventListener('change', this._onRealQuantityChange);
    }
    this._syncQuantityFromReal();
  }

  _normaliseQuantity(value) {
    const source = this.realQuantityInput;
    const min = Number(source?.min || source?.dataset?.min || 1) || 1;
    const step = Number(source?.step || 1) || 1;
    const parsedMax = Number(source?.max || source?.dataset?.max);
    const max = Number.isFinite(parsedMax) && parsedMax > 0 ? parsedMax : Infinity;
    const parsedValue = Number(value);
    const requested = Number.isFinite(parsedValue) ? parsedValue : min;
    const stepped = min + (Math.round((requested - min) / step) * step);
    return Math.min(max, Math.max(min, stepped));
  }

  _setRealQuantity(value) {
    this._bindRealQuantityInput();
    if (!this.realQuantityInput) return;
    const nextValue = this._normaliseQuantity(value);
    const changed = Number(this.realQuantityInput.value) !== nextValue;
    this.realQuantityInput.value = String(nextValue);
    if (changed) {
      this.realQuantityInput.dispatchEvent(new Event('input', { bubbles: true }));
      this.realQuantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
    this._syncQuantityFromReal();
  }

  _syncQuantityFromReal() {
    if (!this.realQuantityInput) return;
    const value = this._normaliseQuantity(this.realQuantityInput.value);
    const min = Number(this.realQuantityInput.min || this.realQuantityInput.dataset?.min || 1) || 1;
    const step = Number(this.realQuantityInput.step || 1) || 1;
    const parsedMax = Number(this.realQuantityInput.max || this.realQuantityInput.dataset?.max);
    const hasMax = Number.isFinite(parsedMax) && parsedMax > 0;

    this.bar.querySelectorAll('[data-sticky-quantity-input]').forEach((input) => {
      input.value = String(value);
      input.min = String(min);
      input.step = String(step);
      if (hasMax) input.max = String(parsedMax);
      else input.removeAttribute('max');
    });

    this.bar.querySelectorAll('[data-sticky-quantity-adjust="-1"]').forEach((button) => {
      button.disabled = value <= min;
    });
    this.bar.querySelectorAll('[data-sticky-quantity-adjust="1"]').forEach((button) => {
      button.disabled = hasMax && value >= parsedMax;
    });
  }

  // The desktop bar uses one combined variant selector. It drives the real
  // on-page option controls and dispatches one native change event, leaving
  // the theme's VariantSelects → ProductInfo pipeline authoritative.
  _setupOptionSync() {
    if (!this.productInfo) return;

    this.bar.querySelectorAll('[data-sticky-variant-select]').forEach((select) => {
      const onChange = () => this._applyVariantChange(select);
      select.addEventListener('change', onChange);
      this._cleanupFns.push(() => select.removeEventListener('change', onChange));
    });
  }

  _applyVariantChange(stickySelect) {
    const selectedOption = stickySelect.selectedOptions[0];
    if (!selectedOption) return;

    let optionValues;
    try {
      optionValues = JSON.parse(selectedOption.dataset.optionValues || '[]');
    } catch (_error) {
      return;
    }

    const realVariantSelects = this.productInfo.querySelector('variant-selects');
    const containers = realVariantSelects?.querySelectorAll('[data-option-name]');
    if (!containers?.length || optionValues.length !== containers.length) return;

    const controls = Array.from(containers).map((container, index) => {
      const newValue = optionValues[index];
      const realSelect = container.querySelector('select');

      if (realSelect) {
        const matchingOption = Array.from(realSelect.options).find((option) => option.value === newValue);
        if (!matchingOption) return null;
        return { type: 'select', control: realSelect, matchingOption };
      }

      const radio = Array.from(container.querySelectorAll('input[type="radio"]')).find((input) => input.value === newValue);
      return radio ? { type: 'radio', control: radio } : null;
    });

    if (controls.some((control) => !control)) return;

    controls.forEach(({ type, control, matchingOption }) => {
      if (type === 'select') {
        Array.from(control.options).forEach((option) => option.removeAttribute('selected'));
        matchingOption.selected = true;
        matchingOption.setAttribute('selected', 'selected');
        return;
      }

      control.checked = true;
    });

    const dispatchTarget = controls[controls.length - 1]?.control;
    if (dispatchTarget) {
      dispatchTarget.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  // Keeps the sticky bar's own selects/price/thumb/availability in sync
  // whenever the variant changes — whether triggered from the sticky bar
  // itself or from the main on-page picker.
  _setupVariantChangeSync() {
    if (typeof subscribe !== 'function') return;
    this._variantChangeUnsub = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
      if (this.sectionId && data.sectionId !== this.sectionId) return;
      this._syncFromVariant(data.variant, data.html);
    });
  }

  _syncFromVariant(variant, html) {
    if (!variant) return;

    this.bar.querySelectorAll('[data-sticky-variant-select]').forEach((select) => {
      const variantId = String(variant.id);
      if (select.value !== variantId) select.value = variantId;
    });

    const priceHtml = html?.querySelector(`#price-${this.sectionId} coretex-price`)?.innerHTML;
    if (priceHtml) {
      this.bar.querySelectorAll('[data-sticky-price], [data-sticky-price-mobile]').forEach((el) => {
        el.innerHTML = priceHtml;
      });
    }

    const thumbSrc = variant.featured_image?.src;
    if (thumbSrc) {
      this.bar.querySelectorAll('[data-sticky-thumb]').forEach((thumb) => {
        thumb.src = thumbSrc + (thumbSrc.includes('?') ? '&' : '?') + 'width=200';
      });
    }

    // The real pipeline (ProductForm.toggleSubmitButton) has already
    // updated the real button's disabled state + label by the time this
    // fires — mirror it rather than re-deriving sold-out logic ourselves.
    this._bindRealQuantityInput();
    this._syncAvailability();
  }

  _syncAvailability() {
    if (!this.triggerButton) return;
    const disabled = this.triggerButton.disabled;
    const label = this.triggerButton.querySelector('.cta')?.textContent?.trim();
    this.bar.querySelectorAll('[data-sticky-add-to-cart]').forEach((btn) => {
      btn.disabled = disabled;
      btn.classList.toggle('is-disabled', disabled);
      const cta = btn.querySelector('.cta');
      if (label && cta) cta.textContent = label;
    });
  }

  destroy() {
    this._observers.forEach((observer) => observer.disconnect());
    this._observers = [];
    this._cleanupFns.forEach((fn) => fn());
    this._cleanupFns = [];
    this.realQuantityInput?.removeEventListener('input', this._onRealQuantityChange);
    this.realQuantityInput?.removeEventListener('change', this._onRealQuantityChange);
    this.realQuantityInput = null;
    if (this._variantChangeUnsub) this._variantChangeUnsub();
    this._variantChangeUnsub = null;
  }
}

let activeStickyBuyBar = null;

function initStickyBuyBar() {
  activeStickyBuyBar?.destroy();
  activeStickyBuyBar = new StickyBuyBar();
}

document.addEventListener('DOMContentLoaded', initStickyBuyBar);

// Shopify Theme Editor: the section's HTML (and #stickyBuyBar with it) is
// fully replaced on edit, so the old instance's observers/listeners would
// otherwise keep pointing at detached nodes. Re-init only for *this*
// product section, and tear down cleanly if the section is removed.
document.addEventListener('shopify:section:load', (event) => {
  const bar = document.getElementById('stickyBuyBar');
  if (!bar || bar.getAttribute('data-section-id') !== event.detail?.sectionId) return;
  initStickyBuyBar();
});

document.addEventListener('shopify:section:unload', (event) => {
  if (!activeStickyBuyBar || activeStickyBuyBar.sectionId !== event.detail?.sectionId) return;
  activeStickyBuyBar.destroy();
  activeStickyBuyBar = null;
});

// Force-close sibling <details> that share the same `name` (Description /
// Shipping & Returns / Review). The browser's native name-based exclusive
// <details> behavior isn't consistently supported, so without this two
// panels could stay visually "open" at once.
(() => {
  document.addEventListener('toggle', (event) => {
    const details = event.target;
    if (!details || details.tagName !== 'DETAILS' || !details.open) return;

    const name = details.getAttribute('name');
    if (!name) return;

    // Scoped to this product's own <product-info> instance, not the whole
    // document — a second product component (or a stale one left behind by
    // a Theme Editor section reload) must never close this one's panels.
    const scope = details.closest('product-info') || details.parentElement;
    if (!scope) return;

    scope.querySelectorAll(`details[name="${CSS.escape(name)}"]`).forEach((other) => {
      if (other !== details) other.open = false;
    });
  }, true);
})();
